import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

const navLinks = [
  { label: '首页', to: '/' },
  { label: '电影', to: '/category/movie' },
  { label: '电视剧', to: '/category/tv' },
  { label: '苹果视频', to: '/category/apple' },
  { label: '关于我们', to: '/about' },
];

export default function Navbar() {
  const navigate = useNavigate();
  const location = useLocation();
  const [keyword, setKeyword] = useState('');
  const [mobileOpen, setMobileOpen] = useState(false);
  const isFirstRender = useRef(true);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    setMobileOpen(false);
  }, [location.pathname]);

  const submitSearch = (event: FormEvent) => {
    event.preventDefault();
    const value = keyword.trim();
    if (!value) return;
    navigate(`/search?q=${encodeURIComponent(value)}`);
    setMobileOpen(false);
  };

  const isActive = (to: string) => {
    if (to === '/') return location.pathname === '/';
    return location.pathname.startsWith(to);
  };

  return (
    <header className="sticky top-0 z-50 border-b border-background-200/80 bg-background-50/95 backdrop-blur">
      <div className="flex h-16 items-center gap-4 px-4 md:px-8">
        <Link to="/" className="flex flex-shrink-0 items-center gap-2">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-500 text-background-50">
            <i className="ri-play-circle-fill text-xl" aria-hidden="true" />
          </span>
          <span className="flex flex-col leading-none">
            <span className="font-heading text-lg font-black tracking-tight text-foreground-950">
              苹果视频
            </span>
            <span className="text-[10px] tracking-widest text-foreground-400">
              APPLE VIDEO
            </span>
          </span>
        </Link>

        <nav className="hidden flex-1 items-center gap-1 lg:flex" aria-label="主导航">
          {navLinks.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              className={`rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                isActive(link.to)
                  ? 'bg-primary-50 text-primary-700'
                  : 'text-foreground-700 hover:bg-background-100 hover:text-primary-600'
              }`}
            >
              {link.label}
            </Link>
          ))}
        </nav>

        <form
          onSubmit={submitSearch}
          className="ml-auto hidden w-full max-w-xs items-center gap-2 rounded-full border border-background-300 bg-background-100 px-3 py-1.5 md:flex"
          role="search"
        >
          <i className="ri-search-line text-base text-foreground-400" aria-hidden="true" />
          <input
            type="search"
            value={keyword}
            onChange={(event) => setKeyword(event.target.value)}
            placeholder="搜索影视或苹果视频"
            className="min-w-0 flex-1 bg-transparent text-sm text-foreground-800 outline-none placeholder:text-foreground-400"
            aria-label="站内搜索"
          />
          <button
            type="submit"
            className="flex-shrink-0 cursor-pointer rounded-full bg-primary-500 px-3 py-1 text-xs font-medium text-background-50 transition-colors hover:bg-primary-600"
          >
            搜索
          </button>
        </form>

        <button
          type="button"
          onClick={() => setMobileOpen((prev) => !prev)}
          aria-label="打开菜单"
          aria-expanded={mobileOpen}
          className="ml-auto flex h-10 w-10 cursor-pointer items-center justify-center rounded-md text-foreground-800 transition-colors hover:bg-background-100 lg:hidden"
        >
          <i className={mobileOpen ? 'ri-close-line text-2xl' : 'ri-menu-line text-2xl'} aria-hidden="true" />
        </button>
      </div>

      {mobileOpen && (
        <div className="border-t border-background-200 bg-background-50 px-4 pb-4 pt-3 lg:hidden">
          <form
            onSubmit={submitSearch}
            className="mb-3 flex items-center gap-2 rounded-full border border-background-300 bg-background-100 px-3 py-2"
            role="search"
          >
            <i className="ri-search-line text-base text-foreground-400" aria-hidden="true" />
            <input
              type="search"
              value={keyword}
              onChange={(event) => setKeyword(event.target.value)}
              placeholder="搜索影视或苹果视频"
              className="min-w-0 flex-1 bg-transparent text-sm text-foreground-800 outline-none placeholder:text-foreground-400"
              aria-label="站内搜索"
            />
            <button type="submit" className="flex-shrink-0 cursor-pointer text-primary-600">
              <i className="ri-arrow-right-line text-lg" aria-hidden="true" />
            </button>
          </form>
          <nav className="grid grid-cols-2 gap-2" aria-label="移动端主导航">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className={`rounded-md px-3 py-2.5 text-sm font-medium transition-colors ${
                  isActive(link.to)
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-700'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
}